import React, { createContext, useContext, useState, useEffect } from 'react';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import logger from '@/utils/logger';
import { getReferralCookie, clearReferralCookie } from '@/utils/referralCookie';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      logger.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    let response;
    try {
      response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
    } catch (networkError) {
      throw new Error('Network error. Please check your connection.');
    }

    let data;
    try {
      const clonedResponse = response.clone();
      const text = await clonedResponse.text();
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      throw new Error(`Server error (${response.status}). Please try again.`);
    }
    
    if (!response.ok) {
      throw new Error(formatApiError(data) || 'Login failed');
    }

    setUser(data);
    // Cookie-driven referral attribution — customer clicked an
    // Ambassador's /join/{slug} link days ago but is only signing in
    // now. Backend never overrides an existing affiliate_id so this is
    // safe to always fire; clear the cookie either way.
    try {
      const refSlug = getReferralCookie();
      if (refSlug) {
        await fetch(`${API_URL}/api/affiliate/attribute-me`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ slug: refSlug }),
        }).catch(() => null);
        clearReferralCookie();
      }
    } catch (e) {
      logger.warn('referral attribute-me skipped:', e);
    }
    return data;
  };

  const register = async (regData) => {
    // Auto-inject referral slug from the 30-day cookie if the caller
    // didn't already pass one (i.e. the regular /signup form rather
    // than /join/{slug}). The backend validates the slug so a stale /
    // suspended one is silently ignored.
    let payload = regData;
    if (!regData.affiliate_slug) {
      const refSlug = getReferralCookie();
      if (refSlug) payload = { ...regData, affiliate_slug: refSlug };
    }
    let response;
    try {
      response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
    } catch (networkError) {
      throw new Error('Network error. Please check your connection.');
    }

    let data;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      throw new Error('Server error. Please try again.');
    }
    
    if (!response.ok) {
      throw new Error(formatApiError(data) || 'Registration failed');
    }

    setUser(data);
    // Attribution complete (whether we passed a slug or not) — cookie
    // shouldn't sit around for another random signup on this device.
    clearReferralCookie();
    return data;
  };

  const loginWithGoogle = async (sessionId) => {
    let response;
    try {
      response = await fetch(`${API_URL}/api/auth/google-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
    } catch (networkError) {
      throw new Error('Network error. Please check your connection.');
    }

    let data;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      throw new Error('Server error. Please try again.');
    }
    
    if (!response.ok) {
      throw new Error(formatApiError(data) || 'Google login failed');
    }

    setUser(data);
    return data;
  };

  const logout = async () => {
    let restoredStaff = false;
    try {
      const res = await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        try {
          const data = await res.json();
          restoredStaff = !!data?.restored_staff;
        } catch (_) { /* empty body is fine */ }
      }
    } catch (error) {
      logger.error('Logout error:', error);
    }
    if (restoredStaff) {
      // Silently restored the impersonator's staff session — re-fetch /me.
      await checkAuth();
    } else {
      setUser(null);
    }
  };

  const impersonate = async (accountCode) => {
    const res = await fetch(`${API_URL}/api/staff/impersonate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ account_code: accountCode })
    });
    let data;
    try { data = await res.json(); } catch (_) { data = {}; }
    if (!res.ok) {
      throw new Error(formatApiError(data) || 'Could not switch to that installer.');
    }
    await checkAuth();
    return data;
  };

  const stopImpersonating = async () => {
    const res = await fetch(`${API_URL}/api/staff/stop-impersonating`, {
      method: 'POST',
      credentials: 'include'
    });
    let data;
    try { data = await res.json(); } catch (_) { data = {}; }
    if (!res.ok) {
      throw new Error(formatApiError(data) || 'Could not exit impersonation.');
    }
    await checkAuth();
    return data;
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager',
    isTeam: user?.role === 'team',
    isStaff: ['admin', 'manager', 'team'].includes(user?.role),
    canViewProfit: !!user?.super_admin || (user?.features ? !!user.features.profit_centre : user?.role === 'admin' || !!user?.can_view_profit),
    isSuperAdmin: !!user?.super_admin,
    can: (feature) => !user?.features || user.features[feature] !== false,
    canManageInvoices: !!user?.super_admin || (user?.features ? !!user.features.invoice_centre : user?.role === 'admin' || !!user?.can_manage_invoices),
    isInstaller: user?.role === 'installer',
    canCreateAccounts: ['admin', 'manager'].includes(user?.role),
    role: user?.role,
    // Impersonation: when staff is acting as an installer, `user` is the installer
    // and `user.impersonator` carries the original staff identity. We expose a
    // convenience flag so components don't have to drill into `user`.
    isImpersonating: !!user?.impersonator,
    impersonator: user?.impersonator || null,
    // Section access - staff always have full access, installers check their access
    hasAccess: (section) => {
      if (!user) return false;
      if (['admin', 'manager', 'team'].includes(user.role)) return true;
      return user.access?.[section] === true;
    },
    access: user?.access || {},
    login,
    register,
    loginWithGoogle,
    logout,
    impersonate,
    stopImpersonating,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
